import { useEffect, useState } from "react";
import { Row, Col, message, Space } from 'antd';
import { EditOutlined } from "@ant-design/icons";
import { getLangList } from "@/lib/i18n";
import SaleConfig from "@/config/sale";
import { ReButton, ReModal } from '@/components/refineElement';
import { showDelConfirm, getOnOkFunction } from "@/lib/antdElementFunction";
import { hasPermission } from "@/lib/permissionUtil";
import DraggableTabs from "@/components/draggableTabs";
import { CategoryType, SaleSubjectType } from './types';
import ModalCategory from './ModalCategory'
import SaleSubject from "./SaleSubject";
import "./index.less";

const {
  getSaleGroupInfo,
  createSaleGroupInfo,
  updateSaleGroupInfo,
  deleteSaleGroupInfo,
  getAllSaleSubjectInfo,
} = SaleConfig;

function Sale() {
  const [categoryArray, setCategoryArray] = useState<CategoryType[]>([]);
  const [saleSubjectArray, setSaleSubjectArray] = useState<SaleSubjectType[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [handleType, setHandleType] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [categoryInfo, setCategoryInfo] = useState({} as CategoryType);
  const saleT: any = getLangList("sale");
  const errorT: any = getLangList("error");
  const okFunction = {
    create: createSaleGroupInfoFunction,
    update: updateSaleGroupInfoFunction,
  };

  useEffect(() => {
    const initData = async () => {
      await getAllSaleSubjectInfoFunction();
      getSaleGroupInfoFunction();
    }

    initData();
  }, []);

  const getSaleGroupInfoFunction = () => {
    getSaleGroupInfo().then((res: any) => {
      const categoryArray = res?.value ?? [];
      setCategoryArray(categoryArray);
      setSelectedGroupId(categoryArray?.[0]?.saleGroupId?.toString() ?? '');
      setSelectedGroupName(categoryArray?.[0]?.saleGroupName?.toString() ?? '');
    });
  };

  const getAllSaleSubjectInfoFunction = async () => {
    return getAllSaleSubjectInfo().then((res: any) => {
      const saleSubjectArray = res?.value ?? [];
      setSaleSubjectArray(saleSubjectArray);
    });
  };

  const toggleModal = (handleType: string = '') => {
    setIsModalVisible(!isModalVisible);
    setHandleType(handleType);
  };

  const clickCreate = () => {
    setCategoryInfo({} as CategoryType);
    toggleModal('create');
  }

  const clickEdit = (data: CategoryType) => (e: any) => {
    e.stopPropagation();
    setCategoryInfo(data);
    toggleModal('update');
  }

  const clickDelete = () => {
    showDelConfirm(() => deleteSaleGroupInfoFunction(selectedGroupId));
  };

  function createSaleGroupInfoFunction(values: CategoryType) {
    const { saleGroupName } = values;
    const params = { saleGroupName };

    createSaleGroupInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getSaleGroupInfoFunction();
    });
  }

  function updateSaleGroupInfoFunction(values: CategoryType) {
    const { saleGroupId, sort } = categoryInfo;
    const { saleGroupName } = values;
    const updateData = {
      saleGroupId,
      saleGroupName,
      sort,
    };
    const params = categoryArray.map((item: CategoryType) => {
      if (item.saleGroupId === saleGroupId) {
        return updateData;
      } else {
        return item;
      }
    });

    if (!hasPermission('SALE_EDIT')) {
      return message.error(errorT.NO_PERMISSION);
    }

    updateSaleGroupInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getSaleGroupInfoFunction();
    });
  }

  function updateSaleGroupSortFunction(params: CategoryType[]) {
    updateSaleGroupInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      getSaleGroupInfoFunction();
    });
  }

  const deleteSaleGroupInfoFunction = (id: string) => {
    const params = { saleGroupId: id };

    deleteSaleGroupInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getSaleGroupInfoFunction();
    });
  }

  const selectGroup = (activeKey: string) => {
    const selectedGroupName = categoryArray?.find((item: any) => item.saleGroupId.toString() === activeKey)?.saleGroupName ?? '';
    setSelectedGroupId(activeKey);
    setSelectedGroupName(selectedGroupName);
  };

  return (
    <>
      <Row className="sale-page">
        <Col span={24}>
          <DraggableTabs
            type="card"
            onChange={selectGroup}
            activeKey={selectedGroupId}
            destroyInactiveTabPane
            items={(
              categoryArray.map((item: any) => {
                const { saleGroupName, saleGroupId } = item;
                const filterSaleSubjectArray = saleSubjectArray.filter((item: SaleSubjectType) => `${item.saleGroupId}` === selectedGroupId);
                return {
                  key: `${saleGroupId}`,
                  label: (
                    <Space>
                      <span>{saleGroupName}</span>
                      {selectedGroupId === saleGroupId.toString() && hasPermission(['SALE_EDIT', 'SALE_DELETE']) &&
                        <span onClick={clickEdit(item)}><EditOutlined /></span>
                      }
                    </Space>
                  ),
                  children: (
                    <SaleSubject
                      categoryArray={categoryArray}
                      selectedGroupId={selectedGroupId}
                      selectedGroupName={selectedGroupName}
                      saleSubjectArray={filterSaleSubjectArray}
                      getAllSaleSubjectInfoFunction={getAllSaleSubjectInfoFunction}
                    />
                  ),
                  apiParams: item,
                }
              })
            )}
            tabBarExtraContent={hasPermission('SALE_NEW') &&
              <ReButton iconName='plus' onClick={clickCreate}>
                {saleT.NEW_CATEGORY}
              </ReButton>
            }
            params={{
              sortParamName: 'sort',
              updateSortFunction: updateSaleGroupSortFunction,
              hasUpdatePermission: hasPermission('SALE_EDIT'),
            }}
          />
        </Col>
      </Row>

      {isModalVisible &&
        <ReModal
          title={saleT.CATEGORY}
          onCancel={toggleModal}
          visible={isModalVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okFunction)}
          CollectedForm={ModalCategory}
          formProps={{
            handleType,
            categoryInfo,
            clickDelete: clickDelete,
          }}
        />
      }
    </>
  )
};

export default Sale;